import * as React from 'react'
import { useQuery } from '@tanstack/react-query'
import type { IrrigationProblemDto } from '@lib/dto/irrigation.dto'
import { irrigationService } from '@/services/IrrigationService'
import { irrigationKeys } from '@/hooks/queryKeys'
import { useTrees } from '@/hooks/useTrees'
import {
  loadLastVisitProblems,
  loadLastVisitTrees,
  saveLastVisitProblems,
  saveLastVisitTrees,
  treeFindings,
  type LastVisitTree,
  type StoredLastVisitProblems,
  type StoredLastVisitTrees,
} from '@/site-visits/lastVisit'
import type { LocalVisit } from '@/site-visits/visitStore'

/**
 * What the visit before this one found, for a revisit's "Last visit" sections
 * (docs/site-visits.md). Null when there is no earlier visit of the kind.
 *
 * Loading and missing are different states, and neither has findings: a
 * caller cannot describe a visit that is still loading and already read.
 */
export type LastVisit<T> =
  | { status: 'loading'; visit: LocalVisit }
  | { status: 'missing'; visit: LocalVisit }
  | { status: 'ready'; visit: LocalVisit; findings: T }

function lastVisitResult<T>(
  previous: LocalVisit,
  findings: T | null,
  isPending: boolean,
): LastVisit<T> {
  if (findings !== null) return { status: 'ready', visit: previous, findings }
  if (isPending) return { status: 'loading', visit: previous }
  return { status: 'missing', visit: previous }
}

/**
 * Last visit's findings on each tree, by tree id. Only trees it found
 * something on are listed.
 */
export function useLastVisitTrees(
  siteId: string,
  previous: LocalVisit | null,
): LastVisit<ReadonlyMap<string, LastVisitTree>> | null {
  const previousId = previous?.id ?? null
  const query = useTrees(siteId, previousId, previousId !== null)
  const stored = React.useMemo(() => loadLastVisitTrees(siteId), [siteId])
  const fromServer = React.useMemo<StoredLastVisitTrees | null>(
    () =>
      query.data === undefined ||
      previousId === null ||
      query.data.visitId !== previousId
        ? null
        : { visitId: previousId, trees: treeFindings(query.data) },
    [query.data, previousId],
  )
  React.useEffect(() => {
    if (fromServer !== null) saveLastVisitTrees(siteId, fromServer)
  }, [siteId, fromServer])
  const kept = fromServer ?? (stored?.visitId === previousId ? stored : null)
  const byTree = React.useMemo(
    () =>
      kept === null ? null : new Map(kept.trees.map((tree) => [tree.id, tree])),
    [kept],
  )
  if (previous === null) return null
  return lastVisitResult(previous, byTree, query.isPending)
}

/** Last visit's irrigation problems, open and fixed. */
export function useLastVisitProblems(
  siteId: string,
  previous: LocalVisit | null,
): LastVisit<readonly IrrigationProblemDto[]> | null {
  const previousId = previous?.id ?? null
  const query = useQuery({
    queryKey: irrigationKeys.site(siteId, previousId),
    queryFn: () => irrigationService.getSite(siteId, previousId),
    enabled: siteId !== '' && previousId !== null,
    // As for the survey's own read: fail once and let the phone's copy stand.
    retry: false,
    staleTime: 30_000,
  })
  const stored = React.useMemo(() => loadLastVisitProblems(siteId), [siteId])
  const fromServer = React.useMemo<StoredLastVisitProblems | null>(
    () =>
      query.data === undefined ||
      previousId === null ||
      query.data.visitId !== previousId
        ? null
        : { visitId: previousId, problems: query.data.problems },
    [query.data, previousId],
  )
  React.useEffect(() => {
    if (fromServer !== null) saveLastVisitProblems(siteId, fromServer)
  }, [siteId, fromServer])
  const kept = fromServer ?? (stored?.visitId === previousId ? stored : null)
  if (previous === null) return null
  return lastVisitResult(
    previous,
    kept?.problems ?? null,
    query.isPending,
  )
}
